import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, interval, of } from 'rxjs';
import { StorageLogs } from '../models/storage';
import { HeaderService } from './header.service';
import { StorageService } from './storage.service';
import { switchMap, catchError, takeWhile } from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
export class ManageStorageLogsService {

  azureUploadRequests = {};
  azureDownloadRequests = {};

  onPremiseUploadRequests = {};
  onPremiseDownloadRequests = {};
  onPremiseZipRequests = {};
  onPremiseUnzipRequests = {};

  gcpUploadRequests = {};
  gcpDownloadRequests = {};

  awsUploadRequests = {};
  awsDownloadRequests = {};

  activeZipJobs = new Map<number, {
    jobId: string,
    vmIp: string,
    userId: string,
    pollingSubscription?: Subscription
  }>();

  activeUnzipJobs = new Map<number, {
    jobId: string,
    vmIp: string,
    userId: string,
    pollingSubscription?: Subscription
  }>();
  constructor(private headerService: HeaderService) { }

  storageLogsStore: StorageLogs[] = [];
  private StorageLogs = new BehaviorSubject<StorageLogs[]>(this.storageLogsStore);
  public storageLogs = this.StorageLogs.asObservable();

  updateStorageLogsStore() {
    this.StorageLogs.next(this.storageLogsStore);
  }

  cancelAzureUploadRequest(uniqueId: number) {
    if (this.azureUploadRequests[uniqueId]) {
      this.azureUploadRequests[uniqueId].abort();
      delete this.azureUploadRequests[uniqueId];
      this.updateTimeRemaining(uniqueId, null)
      this.updateIsRequestComplete(uniqueId, true);
      this.updateToShowAtStart(uniqueId, false);
      let idx = this.storageLogsStore.map(obj => obj.uniqueId).lastIndexOf(uniqueId)
      if (idx != -1) {
        this.storageLogsStore[idx].status = 'Cancelled';
        this.updateStorageLogsStore();
        this.removeLogFromStorageStore(uniqueId);
      }
    }
  }

  cancelAzureDownloadRequest(uniqueId: number) {
    if (this.azureDownloadRequests[uniqueId]) {
      
      const requestData = this.azureDownloadRequests[uniqueId];
      
      if (requestData.abort && typeof requestData.abort === 'function') {
        requestData.abort(); // This will handle the status update
      } else if (requestData.cancelSubject) {
        requestData.cancelSubject.next();
        requestData.cancelSubject.complete();
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      } else if (requestData.signal && requestData.signal.abort) {
        requestData.signal.abort();
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      } else {
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      }
      
      delete this.azureDownloadRequests[uniqueId];
      
    } else {
      // Still update the log status even if we can't find the request
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
    }
  }

  createAbortController(uniqueId: number, forUpload: boolean) {
    if (forUpload) {
      this.azureUploadRequests[uniqueId] = new AbortController();
    } else {
      this.azureDownloadRequests[uniqueId] = {
        signal: new AbortController(),
        abort: function() {
          if (this.signal && this.signal.abort) {
            this.signal.abort();
          }
        }
      };
      this.onPremiseDownloadRequests[uniqueId] = new Subject();
    }
  }
  
  findUniqueIdWithFileName(fileName: string){
    let idx = this.storageLogsStore.map(obj => obj.fileName).lastIndexOf(fileName)
    if(idx != -1){
      return this.storageLogsStore[idx].uniqueId;
    }
    return -1;
  }

  createOnPremiseUploadRequest(uniqueId: number){
    this.onPremiseUploadRequests[uniqueId] = new Subject();
  }
  
  cancelOnPremiseUploadRequest(uniqueId){
      const uploadRequest = this.onPremiseUploadRequests[uniqueId];
      
      if (uploadRequest) {
          uploadRequest.next();
          uploadRequest.complete();
          delete this.onPremiseUploadRequests[uniqueId]; // Clean up after use
      }
      
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Cancelled');
  }

  createOnPremiseDownloadRequest(uniqueId: number) {
    this.onPremiseDownloadRequests[uniqueId] = new Subject();
  }
  
  cancelOnPremiseDownloadRequest(uniqueId: number) {
    if (this.onPremiseDownloadRequests[uniqueId]) {
      const subject = this.onPremiseDownloadRequests[uniqueId];
      
      subject.next();
      subject.complete();
      
      delete this.onPremiseDownloadRequests[uniqueId];
      
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Download Cancelled');
      
    } 
  }

  cancelNg2UploadOnPremise(uniqueId){
    this.updateTimeRemaining(uniqueId, null)
    this.updateRequestCompleteFields(uniqueId, 'Cancelled')
  }

  createOnPremiseZipRequest(uniqueId: number) {
    this.onPremiseZipRequests[uniqueId] = new Subject();
  }
  cancelOnPremiseZipRequest(uniqueId: number) {
    if (this.onPremiseZipRequests[uniqueId]) {
      const subject = this.onPremiseZipRequests[uniqueId];
      subject.next();
      subject.complete();
      delete this.onPremiseZipRequests[uniqueId];
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Zipping Cancelled');
    }
  }

  createOnPremiseUnzipRequest(uniqueId: number) {
    this.onPremiseUnzipRequests[uniqueId] = new Subject();
  }

  cancelOnPremiseUnzipRequest(uniqueId: number) {
    if (this.onPremiseUnzipRequests[uniqueId]) {
      const subject = this.onPremiseUnzipRequests[uniqueId];
      subject.next();
      subject.complete();
      delete this.onPremiseUnzipRequests[uniqueId];
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Unzipping Cancelled');
    }
  }

  cancelOracleUpload(uniqueId){
    this.updateTimeRemaining(uniqueId, null)
    this.updateRequestCompleteFields(uniqueId, 'Cancelled')
  }

  // GCP specific methods
  createGcpUploadRequest(uniqueId: number){
    this.gcpUploadRequests[uniqueId] = new Subject();
  }

  createGcpDownloadRequest(uniqueId: number){
    this.gcpDownloadRequests[uniqueId] = new Subject();
  }

  cancelGcpUploadRequest(uniqueId: number) {
    if (this.gcpUploadRequests[uniqueId]) {
      const requestData = this.gcpUploadRequests[uniqueId];
      
      if (requestData.next && typeof requestData.next === 'function') {
        requestData.next();
        requestData.complete();
      }
      
      delete this.gcpUploadRequests[uniqueId];
      
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Upload Cancelled');
      
    } else {
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Upload Cancelled');
    }
  }

  cancelGcpDownloadRequest(uniqueId: number) {
    if (this.gcpDownloadRequests[uniqueId]) {
      const requestData = this.gcpDownloadRequests[uniqueId];
      if (requestData.abort && typeof requestData.abort === 'function') {
        requestData.abort(); // This will handle the status update
      } else if (requestData.cancelSubject) {
        requestData.cancelSubject.next();
        requestData.cancelSubject.complete();
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      } else if (requestData.signal && requestData.signal.abort) {
        requestData.signal.abort();
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      } else {
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      }
      
      delete this.gcpDownloadRequests[uniqueId];
      
    } else {
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
    }
  }

  // AWS specific methods
  createAwsUploadRequest(uniqueId: number) {
    this.awsUploadRequests[uniqueId] = new Subject();
  }

  createAwsDownloadRequest(uniqueId: number) {
    this.awsDownloadRequests[uniqueId] = new Subject();
  }

  cancelAwsUploadRequest(uniqueId: number) {
    if (this.awsUploadRequests[uniqueId]) {
      const requestData = this.awsUploadRequests[uniqueId];
      
      if (requestData.next && typeof requestData.next === 'function') {
        requestData.next();
        requestData.complete();
      }
      
      delete this.awsUploadRequests[uniqueId];
      
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Upload Cancelled');
      
    } else {
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Upload Cancelled');
    }
  }

  cancelAwsDownloadRequest(uniqueId: number) {
    if (this.awsDownloadRequests[uniqueId]) {
      const requestData = this.awsDownloadRequests[uniqueId];
      // Handle different types of cancellation mechanisms for GCP
      if (requestData.abort && typeof requestData.abort === 'function') {
        // AbortController style
        requestData.abort(); // This will handle the status update
      } else if (requestData.cancelSubject) {
        // Subject style
        requestData.cancelSubject.next();
        requestData.cancelSubject.complete();
        // Update status for subject-based cancellation
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      } else if (requestData.signal && requestData.signal.abort) {
        // Direct AbortController
        requestData.signal.abort();
        // Update status for direct abort
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      } else {
        // Fallback status update
        this.updateTimeRemaining(uniqueId, null);
        this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
      }
      
      // Clean up
      delete this.awsDownloadRequests[uniqueId];
      
    } else {
      // Still update the log status even if we can't find the request
      this.updateTimeRemaining(uniqueId, null);
      this.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
    }
  }

  findIndexOfLog(uniqueId: number){
    return this.storageLogsStore.map(obj => obj.uniqueId).lastIndexOf(uniqueId)
  }

  updateStatusOfLog(uniqueId: number, status: string) {
    let idx = this.findIndexOfLog(uniqueId)
    if(idx != -1) {
      this.storageLogsStore[idx].status = String(status);
    }
  }

  updateIsRequestComplete(uniqueId: number, value: boolean){
    let idx = this.findIndexOfLog(uniqueId)
    if(idx != -1){
      this.storageLogsStore[idx].isRequestCompleted = value;
    } 
  }

  updateToShowAtStart(uniqueId: number, value: boolean){
    let idx = this.findIndexOfLog(uniqueId)
    if(idx != -1) {
      this.storageLogsStore[idx].toShowAtStart = value;
    }
  }

  updateToShowAllStart(){
    for(const idx in this.storageLogsStore){
      this.storageLogsStore[idx].toShowAtStart = true;
    }
    this.updateStorageLogsStore();
  }

  updateTimeRemaining(uniqueId: number, value: number){
    let idx = this.findIndexOfLog(uniqueId)
    if(idx != -1) {
      this.storageLogsStore[idx].timeRemaining = value;
    }
  }

  disableToShowAtStart(){
    for(const idx in this.storageLogsStore){
      if(this.storageLogsStore[idx].isRequestCompleted){
        this.storageLogsStore[idx].toShowAtStart = false;
        this.removeLogFromStorageStore(Number(idx));
      } 
    }
    this.updateStorageLogsStore();
  }

  removeLogFromStorageStore(uniqueId: number){
    let idx = this.findIndexOfLog(uniqueId)
    if(idx != -1) {
      this.storageLogsStore.splice(idx,1);
    }
  }

  addLogToStorageLogStore(newLogObj: StorageLogs) {
    this.storageLogsStore.push(newLogObj)
    this.updateStorageLogsStore();
  }

  createLogObject(
    uniqueId: number,
    deployment: string,
    logType: number,
    fileName: string,
    actionPath: string,
    message: string,
    status: string,
    showLogActions: boolean,
    jobId?: string // Add optional jobId parameter
  ): StorageLogs {
    let logObj: StorageLogs = {
      uniqueId: uniqueId,
      deployment: deployment,
      logType: logType,
      fileName: fileName,
      actionPath: actionPath,
      message: message,
      status: status,
      showLogActions: showLogActions,
      actionTime: String(this.headerService.getUserNewDate().toLocaleString()),
      toShowAtStart: false,
      isRequestCompleted: false,
      timeRemaining: null,
      jobId: jobId // Store the jobId
    }
    return logObj;
  }

  updateRequestCompleteFields(uniqueId: number, statusOfLog: string) {
    this.updateStatusOfLog(uniqueId, statusOfLog);
    this.updateIsRequestComplete(uniqueId, true);
    this.updateStorageLogsStore();
    let idx = this.findIndexOfLog(uniqueId);
    if(idx != -1 && !this.storageLogsStore[idx].toShowAtStart){
      this.removeLogFromStorageStore(uniqueId);
    }
  }

  checkIfRequestIsCompleted(uniqueIds: number[]){
    let isAllRequestCompletionPending: boolean = true;
    while(isAllRequestCompletionPending){
      let logs: number[] = this.StorageLogs.value.filter(log => uniqueIds.includes(log?.uniqueId) && log.isRequestCompleted).map(log => log.uniqueId);
      if(logs.length == 0){
        isAllRequestCompletionPending = false;
      }
    }
  }


  // Add new methods for job management
  addActiveZipJob(uniqueId: number, jobId: string, vmIp: string, userId: string) {
    this.activeZipJobs.set(uniqueId, { jobId, vmIp, userId });
  }

  setZipJobPolling(uniqueId: number, subscription: Subscription) {
    const jobInfo = this.activeZipJobs.get(uniqueId);
    if (jobInfo) {
      jobInfo.pollingSubscription = subscription;
    }
  }

  getActiveZipJob(uniqueId: number) {
    return this.activeZipJobs.get(uniqueId);
  }

  removeActiveZipJob(uniqueId: number) {
    const jobInfo = this.activeZipJobs.get(uniqueId);
    if (jobInfo && jobInfo.pollingSubscription) {
      jobInfo.pollingSubscription.unsubscribe();
    }
    this.activeZipJobs.delete(uniqueId);
  }

  // Enhanced method to clean up all job subscriptions
  cleanupAllZipJobs() {
    this.activeZipJobs.forEach((jobInfo, uniqueId) => {
      if (jobInfo.pollingSubscription) {
        jobInfo.pollingSubscription.unsubscribe();
      }
    });
    this.activeZipJobs.clear();
  }

  // Update existing method
  updateLogJobId(uniqueId: number, jobId: string) {
    const logIndex = this.findIndexOfLog(uniqueId);
    if (logIndex !== -1) {
      this.storageLogsStore[logIndex].jobId = jobId;
      this.updateStorageLogsStore();
    }
  }

  // Keep existing method
  getLogByUniqueId(uniqueId: number): StorageLogs | null {
    const logIndex = this.findIndexOfLog(uniqueId);
    return logIndex !== -1 ? this.storageLogsStore[logIndex] : null;
  }

  // Active unzip job management
  addActiveUnzipJob(uniqueId: number, jobId: string, vmIp: string, userId: string) {
    this.activeUnzipJobs.set(uniqueId, { jobId, vmIp, userId });
  }

  setUnzipJobPolling(uniqueId: number, subscription: Subscription) {
    const jobInfo = this.activeUnzipJobs.get(uniqueId);
    if (jobInfo) {
      jobInfo.pollingSubscription = subscription;
    }
  }

  getActiveUnzipJob(uniqueId: number) {
    return this.activeUnzipJobs.get(uniqueId);
  }


  removeActiveUnzipJob(uniqueId: number) {
    const jobInfo = this.activeUnzipJobs.get(uniqueId);
    if (jobInfo && jobInfo.pollingSubscription) {
      jobInfo.pollingSubscription.unsubscribe();
    }
    this.activeUnzipJobs.delete(uniqueId);
  }

  cleanupAllUnzipJobs() {
    this.activeUnzipJobs.forEach((jobInfo, uniqueId) => {
      if (jobInfo.pollingSubscription) {
        jobInfo.pollingSubscription.unsubscribe();
      }
    });
    this.activeUnzipJobs.clear();
  }

  cleanupAllJobs() {
    this.cleanupAllZipJobs();
    this.cleanupAllUnzipJobs();
  }

  getLogById(uniqueId: number): StorageLogs | undefined {
    const idx = this.findIndexOfLog(uniqueId);
    return idx !== -1 ? this.storageLogsStore[idx] : undefined;
  }

  isOperationComplete(uniqueId: number): boolean {
    const log = this.getLogById(uniqueId);
    return log ? log.isRequestCompleted : false;
  }
  
}
